/**
 * Client-side service for communicating with the Telegram API worker
 *
 * This service makes requests to the Cloudflare Pages Function
 * which securely handles Telegram API calls server-side
 */

import type { Option, Section, Currency, Locale } from '../types';
import { read_json_body, http_error_message } from '../utils/http';

export interface QuestionResponseItem {
  question: string;
  answer: string;
  questionId: string;
}

export interface SendTelegramRequest {
  companyName: string;
  selectedOptions: Option[];
  hourlyOptions: Option[];
  sections: Section[];
  totalPrice: number;
  currency: Currency;
  projectDeadline: number;
  locale: Locale;
  questionResponses?: QuestionResponseItem[]; // Optional array of question responses
}

export interface SendTelegramResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Sends project summary to Telegram via the Pages Function
 *
 * @param data - Project summary data
 * @returns Promise that resolves when message is sent
 * @throws Error if the request fails
 */
export async function sendProjectSummary(data: SendTelegramRequest): Promise<void> {
  try {
    const response = await fetch('/api/send-telegram', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    const result = await read_json_body<SendTelegramResponse>(response);

    if (!response.ok || !result) {
      throw new Error(result?.error || http_error_message(response, result !== null));
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to send message to Telegram');
    }
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network error: Failed to send message to Telegram');
  }
}
